/**
 * Atendimentos (spec 018, US2): encontrar e comparar.
 *
 * Medido em `5c943de`: a lista forçava UMA mesa por vez, não tinha filtro de
 * responsável, SLA nem "meus", e o voltar do ticket perdia a mesa escolhida.
 * Aqui: todas as mesas por padrão, seis visões (o mesmo predicado dos
 * indicadores do Hoje), busca por número/assunto/cliente/contato/identificador
 * do recurso em qualquer mesa, filtros como marcadores removíveis e TUDO na
 * URL. Cinco colunas: Atendimento, Situação, Responsável, Prazo, Atualização.
 * Seleção abre as ações em massa (atribuir, mover etapa) — pela MESMA escrita
 * do Completo (PATCH do ticket), com as mesmas guardas (FR-006).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDownUp, Filter, Search, X } from 'lucide-react';
import {
  TICKET_SITUATIONS,
  capitalize,
  situationLabel,
  type TicketListItem,
  type TicketSituation,
} from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input, Select } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/misc';
import { useTransientToast } from '@/components/ui/transient-toast';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthUser } from '@/auth/use-auth';
import { api } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { useTerms } from '@/shared/tenant-profile';
import { deskDetailQuery, desksListQuery, ticketsListQuery } from '@/features/tickets/tickets-queries';
import { teamListQuery } from '@/features/team/team-queries';
import { useDebouncedValue } from '@/features/tickets/use-debounced-value';
import { errorMessage } from '@/features/tickets/ticket-format';
import { formatRelativeTime } from '@/features/chats/chats-format';
import { TONE_TEXT, dueInfo } from '@/features/work/work-format';
import { SituationChip } from './situation-chip';
import {
  PAGE_SIZE,
  VIEW_OF_VISAO,
  VISOES,
  atendimentosSearchSchema,
  effectiveSort,
  rememberList,
  type AtendimentosSearch,
  type Visao,
} from './atendimentos-search';

const VISAO_LABEL = (v: Visao, client: string): string =>
  ({
    minha_fila: 'Minha fila',
    precisa_resposta: 'Precisa de resposta',
    prazo_em_risco: 'Prazo em risco',
    sem_responsavel: 'Sem responsável',
    aguardando_cliente: `Aguardando ${client}`,
    todos: 'Todos',
  })[v];

const STATUS_LABEL: Record<AtendimentosSearch['status'], string> = {
  open: 'Abertos',
  closed: 'Fechados',
  canceled: 'Cancelados',
  all: 'Todos os status',
};

export function AtendimentosPage() {
  const search = atendimentosSearchSchema.parse(useSearch({ strict: false }));
  const navigate = useNavigate();
  const terms = useTerms();
  const me = useAuthUser();
  const qc = useQueryClient();
  // o resultado das ações em massa sobrevive à seleção que se desfaz
  const { toast, showToast } = useTransientToast('info', 8000);

  const setSearch = (patch: Partial<AtendimentosSearch>, opts: { keepPage?: boolean } = {}) =>
    void navigate({
      to: '/s/atendimentos',
      // mudar filtro volta à 1ª página; paginar não
      search: { ...search, ...(opts.keepPage ? {} : { pagina: 1 }), ...patch },
      replace: true,
    });

  // a lista que o "voltar" do atendimento devolve
  useEffect(() => rememberList(search), [search]);

  const [qInput, setQInput] = useState(search.q ?? '');
  const debounced = useDebouncedValue(qInput, 300);
  useEffect(() => {
    if ((debounced || undefined) !== search.q) setSearch({ q: debounced || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const desksQ = useQuery(desksListQuery());
  // o filtro de responsável: a equipe ativa deste tenant (pedido da PoolTrack, 05/10)
  const usersQ = useQuery(teamListQuery());
  const desks = desksQ.data ?? [];
  const users = usersQ.data?.items ?? [];

  const view = VIEW_OF_VISAO[search.visao];
  const { sort, order } = effectiveSort(search);
  const listQ = useQuery({
    ...ticketsListQuery({
      view,
      ...(search.escopo === 'meu' ? { scope: 'mine' as const } : {}),
      desk_ids: search.mesas,
      responsible_ids: search.resp,
      ...(search.situacoes?.length ? { situations: search.situacoes.join(',') } : {}),
      // as visões de trabalho já olham só os abertos
      status: view === 'all' ? search.status : 'all',
      search: search.q || undefined,
      sort,
      order,
      limit: PAGE_SIZE,
      offset: (search.pagina - 1) * PAGE_SIZE,
    }),
    placeholderData: keepPreviousData,
  });

  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // seleção só vale para a lista que se está vendo
  const pageKey = JSON.stringify(search);
  useEffect(() => setSelected(new Set()), [pageKey]);
  const now = new Date();

  const toggle = (n: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
  const allOnPage = items.length > 0 && items.every((t) => selected.has(t.ticketNumber));

  const deskName = (id: number) => desks.find((d) => d.id === id)?.displayName ?? `#${id}`;
  const userName = (id: number) => (id === me?.id ? 'Eu' : (users.find((u) => u.id === id)?.name ?? `#${id}`));

  const markers: Array<{ key: string; label: string; clear: () => void }> = [
    ...(search.escopo === 'meu'
      ? [{ key: 'escopo', label: 'Meus e sem responsável', clear: () => setSearch({ escopo: undefined }) }]
      : []),
    ...(search.mesas ?? []).map((id) => ({
      key: `mesa-${id}`,
      label: `${capitalize(terms.desk)}: ${deskName(id)}`,
      clear: () => setSearch({ mesas: search.mesas?.filter((x) => x !== id) }),
    })),
    ...(search.resp ?? []).map((id) => ({
      key: `resp-${id}`,
      label: `Responsável: ${userName(id)}`,
      clear: () => setSearch({ resp: search.resp?.filter((x) => x !== id) }),
    })),
    ...(search.situacoes ?? []).map((s) => ({
      key: `sit-${s}`,
      label: situationLabel(terms, s),
      clear: () => setSearch({ situacoes: search.situacoes?.filter((x) => x !== s) }),
    })),
    ...(view === 'all' && search.status !== 'open'
      ? [{ key: 'status', label: STATUS_LABEL[search.status], clear: () => setSearch({ status: 'open' }) }]
      : []),
    ...(search.q ? [{ key: 'q', label: `Busca: ${search.q}`, clear: () => (setQInput(''), setSearch({ q: undefined })) }] : []),
  ];

  const toggleIn = <T,>(list: T[] | undefined, value: T): T[] | undefined => {
    const next = list?.includes(value) ? list.filter((x) => x !== value) : [...(list ?? []), value];
    return next.length > 0 ? next : undefined;
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-foreground">{capitalize(terms.tickets)}</h1>
        <span className="text-[13px] text-muted-foreground tabular-nums">
          {listQ.isPending ? '' : `${total} ${total === 1 ? terms.ticket : terms.tickets}`}
        </span>
        {listQ.isFetching && !listQ.isPending ? <Spinner className="size-3.5" /> : null}
        <div className="relative ml-auto w-full sm:w-80">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder={`Nº, assunto, ${terms.client}, ${terms.contact} ou ${terms.resourceId}`}
            aria-label={`Buscar ${terms.tickets}`}
            className="h-9 pl-8 text-[13px]"
          />
        </div>
      </div>

      {/* visões: o mesmo predicado dos indicadores do Hoje */}
      <div className="flex flex-wrap items-center gap-1" role="tablist" aria-label="Visões">
        {VISOES.map((v) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={search.visao === v}
            onClick={() => setSearch({ visao: v, ordem: undefined, sentido: undefined })}
            className={cn(
              'inline-flex h-8 items-center rounded-md border px-3 text-[13px] font-medium transition-colors',
              search.visao === v
                ? 'border-transparent bg-foreground text-background'
                : 'border-border bg-card text-foreground hover:bg-muted/60'
            )}
          >
            {VISAO_LABEL(v, terms.client)}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Filter />
              Filtrar
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>{capitalize(terms.desk)}</DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="max-h-80 overflow-y-auto">
                {desks
                  .filter((d) => d.active || search.mesas?.includes(d.id))
                  .map((d) => (
                    <DropdownMenuCheckboxItem
                      key={d.id}
                      checked={search.mesas?.includes(d.id) ?? false}
                      onSelect={(e) => e.preventDefault()}
                      onCheckedChange={() => setSearch({ mesas: toggleIn(search.mesas, d.id) })}
                    >
                      {d.displayName}
                    </DropdownMenuCheckboxItem>
                  ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Responsável</DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="max-h-80 overflow-y-auto">
                {me ? (
                  <DropdownMenuCheckboxItem
                    checked={search.resp?.includes(me.id) ?? false}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={() => setSearch({ resp: toggleIn(search.resp, me.id) })}
                  >
                    Eu
                  </DropdownMenuCheckboxItem>
                ) : null}
                {users
                  .filter((u) => u.id !== me?.id)
                  .map((u) => (
                    <DropdownMenuCheckboxItem
                      key={u.id}
                      checked={search.resp?.includes(u.id) ?? false}
                      onSelect={(e) => e.preventDefault()}
                      onCheckedChange={() => setSearch({ resp: toggleIn(search.resp, u.id) })}
                    >
                      {u.name}
                    </DropdownMenuCheckboxItem>
                  ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Situação</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {TICKET_SITUATIONS.map((s: TicketSituation) => (
                  <DropdownMenuCheckboxItem
                    key={s}
                    checked={search.situacoes?.includes(s) ?? false}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={() => setSearch({ situacoes: toggleIn(search.situacoes, s) })}
                  >
                    {situationLabel(terms, s)}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            {view === 'all' ? (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Status</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuRadioGroup
                    value={search.status}
                    onValueChange={(v) => setSearch({ status: v as AtendimentosSearch['status'] })}
                  >
                    {(Object.keys(STATUS_LABEL) as Array<AtendimentosSearch['status']>).map((s) => (
                      <DropdownMenuRadioItem key={s} value={s}>
                        {STATUS_LABEL[s]}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>

        {markers.map((m) => (
          <span
            key={m.key}
            className="inline-flex h-7 items-center gap-1 rounded-full border bg-secondary pr-1 pl-3 text-xs font-medium text-secondary-foreground"
          >
            {m.label}
            <button
              type="button"
              onClick={m.clear}
              aria-label={`Remover filtro ${m.label}`}
              className="rounded-full p-0.5 hover:bg-primary/15"
            >
              <X className="size-3" />
            </button>
          </span>
        ))}

        <div className="ml-auto flex items-center gap-1.5">
          <ArrowDownUp className="size-3.5 text-muted-foreground" aria-hidden />
          <Select
            aria-label="Ordenar por"
            value={sort === 'created_at' ? 'criacao' : sort === 'awaiting' ? 'espera' : 'atualizacao'}
            onChange={(e) => setSearch({ ordem: e.target.value as AtendimentosSearch['ordem'], sentido: undefined })}
            className="h-8 w-44 text-[13px]"
          >
            <option value="atualizacao">Atualização</option>
            <option value="criacao">Abertura</option>
            <option value="espera">Espera do cliente</option>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSearch({ sentido: order === 'desc' ? 'asc' : 'desc' })}
            aria-label={order === 'desc' ? 'Mais recentes primeiro' : 'Mais antigos primeiro'}
            title={order === 'desc' ? 'Mais recentes primeiro' : 'Mais antigos primeiro'}
          >
            {order === 'desc' ? '↓' : '↑'}
          </Button>
        </div>
      </div>

      {selected.size > 0 ? (
        <BulkBar
          selected={items.filter((t) => selected.has(t.ticketNumber))}
          users={users}
          onDone={(message) => {
            setSelected(new Set());
            showToast(message);
            void qc.invalidateQueries({ queryKey: ['erp', 'tickets'] });
          }}
          onClear={() => setSelected(new Set())}
        />
      ) : null}

      <div className="overflow-x-auto rounded-lg border bg-card shadow-sm">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b bg-muted/50 text-left text-xs font-semibold text-muted-foreground">
              <th className="w-10 px-3 py-2">
                <Checkbox
                  aria-label="Selecionar todos da página"
                  checked={allOnPage}
                  onCheckedChange={(v) =>
                    setSelected(v === true ? new Set(items.map((t) => t.ticketNumber)) : new Set())
                  }
                />
              </th>
              <th className="px-3 py-2">{capitalize(terms.ticket)}</th>
              <th className="px-3 py-2">Situação</th>
              <th className="px-3 py-2">Responsável</th>
              <th className="px-3 py-2">Prazo</th>
              <th className="px-3 py-2 text-right">Atualização</th>
            </tr>
          </thead>
          <tbody>
            {listQ.isPending ? (
              [0, 1, 2, 3, 4].map((i) => (
                <tr key={i} className="border-b last:border-0">
                  <td colSpan={6} className="px-3 py-3">
                    <Skeleton className="h-8 w-full" />
                  </td>
                </tr>
              ))
            ) : listQ.isError ? (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-sm text-destructive">
                  {errorMessage(listQ.error)}
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-12 text-center text-sm text-muted-foreground">
                  Nenhum {terms.ticket} nesta visão.{' '}
                  {search.visao !== 'todos' || markers.length > 0 ? (
                    <Link to="/s/atendimentos" search={{ visao: 'todos' }} className="font-medium text-primary hover:underline">
                      Ver todos
                    </Link>
                  ) : null}
                </td>
              </tr>
            ) : (
              items.map((t) => (
                <Row
                  key={t.ticketNumber}
                  t={t}
                  now={now}
                  selected={selected.has(t.ticketNumber)}
                  onToggle={() => toggle(t.ticketNumber)}
                  onOpen={() =>
                    void navigate({
                      to: '/s/atendimentos/$ticketNumber',
                      params: { ticketNumber: String(t.ticketNumber) },
                    })
                  }
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE ? (
        <div className="flex items-center justify-between text-[13px] text-muted-foreground">
          <span className="tabular-nums">
            {(search.pagina - 1) * PAGE_SIZE + 1}–{Math.min(search.pagina * PAGE_SIZE, total)} de {total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={search.pagina <= 1}
              onClick={() => setSearch({ pagina: search.pagina - 1 }, { keepPage: true })}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={search.pagina * PAGE_SIZE >= total}
              onClick={() => setSearch({ pagina: search.pagina + 1 }, { keepPage: true })}
            >
              Próxima
            </Button>
          </div>
        </div>
      ) : null}
      {toast}
    </div>
  );
}

function Row({
  t,
  now,
  selected,
  onToggle,
  onOpen,
}: {
  t: TicketListItem;
  now: Date;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
}) {
  const due = dueInfo(t, now);
  const context = [t.client.name, t.resourceLabel].filter(Boolean).join(' · ');
  return (
    <tr
      className={cn('cursor-pointer border-b transition-colors last:border-0 hover:bg-muted/40', selected && 'bg-secondary/60')}
      onClick={onOpen}
    >
      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <Checkbox aria-label={`Selecionar #${t.ticketNumber}`} checked={selected} onCheckedChange={onToggle} />
      </td>
      <td className="max-w-[28rem] px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="shrink-0 font-semibold text-primary tabular-nums">#{t.ticketNumber}</span>
          <span className="truncate font-medium text-foreground" title={t.title}>
            {t.title}
          </span>
          {t.awaitingReplySince ? (
            <span className="shrink-0 rounded-full bg-[#FFF7E6] px-1.5 py-0.5 text-[10px] font-semibold text-[#D48806]">
              aguarda resposta
            </span>
          ) : null}
        </div>
        <div className="truncate text-xs text-muted-foreground" title={context}>
          {context} · {t.desk.name}
        </div>
      </td>
      <td className="px-3 py-2">
        <SituationChip situation={t.situation} stuck={t.stuckInStage} />
      </td>
      <td className="max-w-40 truncate px-3 py-2">
        {t.responsible ? t.responsible.name : <span className="text-muted-foreground">—</span>}
      </td>
      <td className={cn('whitespace-nowrap px-3 py-2 text-xs font-medium', TONE_TEXT[due.tone])}>{due.text}</td>
      <td className="whitespace-nowrap px-3 py-2 text-right text-xs text-muted-foreground tabular-nums">
        {formatRelativeTime(t.activityAt)}
      </td>
    </tr>
  );
}

/**
 * Ações em massa — só com seleção (US2 AS4). Uma escrita por ticket pelo PATCH
 * de sempre: mesa espelhada, permissão e etapa de outra mesa são recusadas
 * pela API como no Completo, e o resultado diz quantos foram e por que não.
 */
function BulkBar({
  selected,
  users,
  onDone,
  onClear,
}: {
  selected: TicketListItem[];
  users: Array<{ id: number; name: string }>;
  onDone: (result: string) => void;
  onClear: () => void;
}) {
  const terms = useTerms();
  const [busy, setBusy] = useState(false);
  const deskIds = useMemo(() => [...new Set(selected.map((t) => t.desk.id))], [selected]);
  const sameDesk = deskIds.length === 1 ? deskIds[0]! : null;
  const deskQ = useQuery({ ...deskDetailQuery(sameDesk ?? 0), enabled: sameDesk !== null });
  const stages = [...(deskQ.data?.stages ?? [])].sort((a, b) => a.position - b.position);

  const apply = async (body: { responsibleId: number | null } | { stageId: number }) => {
    setBusy(true);
    const failures = new Map<string, number>();
    let ok = 0;
    for (const t of selected) {
      const res = await api(`/tickets/${t.ticketNumber}`, { method: 'PATCH', body });
      if (res.error) {
        const msg = errorMessage(res.error);
        failures.set(msg, (failures.get(msg) ?? 0) + 1);
      } else ok++;
    }
    setBusy(false);
    const failed = [...failures].map(([msg, n]) => `${n} com "${msg}"`).join('; ');
    onDone(`${ok} atualizado${ok === 1 ? '' : 's'}${failed ? ` · não foram: ${failed}` : ''}`);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/40 bg-secondary px-3 py-2 text-[13px]">
      <span className="font-semibold text-secondary-foreground">
        {selected.length} selecionado{selected.length === 1 ? '' : 's'}
      </span>
      <Select
        aria-label="Atribuir a"
        disabled={busy}
        value=""
        onChange={(e) => {
          const v = e.target.value;
          if (v === '') return;
          void apply({ responsibleId: v === 'none' ? null : Number(v) });
        }}
        className="h-8 w-48 text-[13px]"
      >
        <option value="">Atribuir a…</option>
        <option value="none">Ninguém (sem responsável)</option>
        {users.map((u) => (
          <option key={u.id} value={u.id}>
            {u.name}
          </option>
        ))}
      </Select>
      <Select
        aria-label={`Mover ${terms.stage}`}
        disabled={busy || sameDesk === null}
        title={sameDesk === null ? `Selecione ${terms.tickets} de uma mesma ${terms.desk} para mover de ${terms.stage}` : undefined}
        value=""
        onChange={(e) => {
          if (e.target.value) void apply({ stageId: Number(e.target.value) });
        }}
        className="h-8 w-52 text-[13px]"
      >
        <option value="">{sameDesk === null ? `Mover ${terms.stage} (uma ${terms.desk})` : `Mover para ${terms.stage}…`}</option>
        {stages.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </Select>
      {busy ? <Spinner className="size-4" /> : null}
      <Button variant="ghost" size="sm" className="ml-auto" onClick={onClear}>
        Limpar seleção
      </Button>
    </div>
  );
}
